import { c as _c } from "react/compiler-runtime";
import { useRef, useCallback, useEffect, useMemo } from "react";
import { ExportDropdown } from "@components/common/export/export-dropdown";
import { useDateTime } from "@hooks/use-date-time";
import { useExportAllPaginated } from "@hooks/use-export-all-paginated";
import { downloadProjectsData } from "@utils/file-data-export";
import { TableType } from "@utils/track-event/track-event-list";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { convertProjectsResponseToSdbProjects } from "@store/projects/projects-slice-utils";
import { projectStateToApiArchivingState } from "@utils/project-utils";
import { selectedGroupSelector } from "@store/groups/groups-selector";
import { selectedSdbCompanySelector } from "@store/sdb-company/sdb-company-selector";
import { preferredAreaUnitSelector } from "@store/settings/settings-selector";
import { useAppSelector } from "@store/store-helper";
import { UtilsTypes } from "@stellar/utils";
/** Dropdown to export projects in the file format selected by the user */
export function BulkExportProjects(t0) {
  const $ = _c(35);
  const {
    companyId,
    subjectType,
    selectedProjects,
    projectStatus,
    areEntitiesSelected,
    buttonType: t1,
    columnsToExclude,
    viewMode
  } = t0;
  const buttonType = t1 === undefined ? "bulk" : t1;
  const {
    formatDate
  } = useDateTime();
  const selectedSdbCompany = useAppSelector(selectedSdbCompanySelector);
  const selectedGroup = useAppSelector(selectedGroupSelector);
  const areaUnit = useAppSelector(preferredAreaUnitSelector);
  const cachedProjectsRef = useRef(null);
  const previousTableCountRef = useRef(null);
  let t2;
  let t3;
  if ($[0] !== areEntitiesSelected || $[1] !== selectedProjects.length) {
    t2 = () => {
      if (areEntitiesSelected) {
        return;
      }
      const currentTableCount = selectedProjects.length;
      if (previousTableCountRef.current === null) {
        previousTableCountRef.current = currentTableCount;
        return;
      }
      if (currentTableCount !== previousTableCountRef.current) {
        cachedProjectsRef.current = null;
        previousTableCountRef.current = currentTableCount;
      }
    };
    t3 = [areEntitiesSelected, selectedProjects.length];
    $[0] = areEntitiesSelected;
    $[1] = selectedProjects.length;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => {
      cachedProjectsRef.current = null;
    };
    t5 = [];
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  const {
    exportAll
  } = useExportAllPaginated();
  let t6;
  bb0: {
    if (subjectType === "group" && selectedGroup) {
      t6 = selectedGroup.name;
      break bb0;
    }
    if (subjectType === "workspace" && selectedSdbCompany) {
      t6 = selectedSdbCompany.name;
      break bb0;
    }
    t6 = "";
  }
  const entityName = t6;
  let t7;
  if ($[6] !== companyId || $[7] !== exportAll || $[8] !== projectStatus || $[9] !== selectedGroup?.id) {
    t7 = async options => {
      const currentStatus = projectStatus ?? UtilsTypes.ProjectArchivingState.active;
      if (cachedProjectsRef.current) {
        return cachedProjectsRef.current;
      }
      const result = await exportAll({
        fetchParams: {
          companyId,
          archivingStates: projectStateToApiArchivingState(currentStatus),
          groupId: selectedGroup?.id
        },
        fetchPage: _temp,
        transform: _temp3,
        onProgress: options?.onProgress,
        signal: options?.signal
      });
      if (result === null) {
        return null;
      }
      cachedProjectsRef.current = result;
      return result;
    };
    $[6] = companyId;
    $[7] = exportAll;
    $[8] = projectStatus;
    $[9] = selectedGroup?.id;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  selectedGroup?.id;
  const fetchAllProjects = t7;
  let t8;
  if ($[11] !== areEntitiesSelected || $[12] !== areaUnit || $[13] !== columnsToExclude || $[14] !== entityName || $[15] !== fetchAllProjects || $[16] !== formatDate || $[17] !== projectStatus || $[18] !== selectedProjects || $[19] !== subjectType) {
    t8 = async (format, options_0) => {
      const fileName = generateFileName(subjectType, entityName, projectStatus);
      const projectsToExport = areEntitiesSelected ? selectedProjects : await fetchAllProjects(options_0);
      if (projectsToExport === null) {
        return null;
      }
      return await downloadProjectsData({
        fileName,
        projects: projectsToExport,
        formatDate,
        format,
        columnsToExclude,
        areaUnit
      });
    };
    $[11] = areEntitiesSelected;
    $[12] = areaUnit;
    $[13] = columnsToExclude;
    $[14] = entityName;
    $[15] = fetchAllProjects;
    $[16] = formatDate;
    $[17] = projectStatus;
    $[18] = selectedProjects;
    $[19] = subjectType;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleExport = t8;
  let t9;
  if ($[21] !== projectStatus || $[22] !== subjectType || $[23] !== viewMode) {
    t9 = exportedTableType(subjectType, projectStatus, viewMode);
    $[21] = projectStatus;
    $[22] = subjectType;
    $[23] = viewMode;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== areEntitiesSelected || $[26] !== selectedProjects.length || $[27] !== t9) {
    t10 = {
      dataType: t9,
      numberOfEntities: selectedProjects.length,
      areEntitiesSelected
    };
    $[25] = areEntitiesSelected;
    $[26] = selectedProjects.length;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      success: "Successfully exported projects.",
      error: "Cannot export projects. Please try again."
    };
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const t12 = !areEntitiesSelected;
  let t13;
  if ($[30] !== buttonType || $[31] !== handleExport || $[32] !== t10 || $[33] !== t12) {
    t13 = <ExportDropdown trackingProps={t10} exportHandler={handleExport} toastMessage={t11} buttonTooltipText="Export projects" buttonType={buttonType} shouldShowExportAllDialog={t12} />;
    $[30] = buttonType;
    $[31] = handleExport;
    $[32] = t10;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
}

/** Returns the file name */
function _temp3(dataArray) {
  return dataArray.flatMap(_temp2);
}
function _temp2(responseData) {
  return convertProjectsResponseToSdbProjects(responseData);
}
async function _temp(params) {
  const response = await faroCoreApiClient.projects.getProjects(params);
  return {
    data: [response.data],
    next: response.page.next
  };
}
function generateFileName(subjectType, entityName, projectStatus) {
  switch (subjectType) {
    case "workspace":
      return `Company ${entityName} - ${projectStatus} project list`;
    case "group":
      return `Group ${entityName} - project list`;
  }
}

/** Returns the exported table type for tracking */
function exportedTableType(subjectType, projectStatus, viewMode) {
  let baseType;
  switch (subjectType) {
    case "workspace":
      baseType = projectStatus === UtilsTypes.ProjectArchivingState.active ? TableType.activeProjects : TableType.archiveProjects;
      break;
    case "group":
      baseType = TableType.groupProjects;
      break;
  }
  return viewMode ? `${baseType} in ${viewMode} view` : baseType;
}